import GlobalStyle from '@rsa-digital/evo-shared-components/globalStyle';
import themeJohnLewis from '@rsa-digital/evo-shared-components/theme/JohnLewis';
import themeMarksAndSpencer from '@rsa-digital/evo-shared-components/theme/MarksAndSpencer';
import themeMoreThan from '@rsa-digital/evo-shared-components/theme/MoreThan';
import themeTesco from '@rsa-digital/evo-shared-components/theme/Tesco';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { CsAsset } from 'types/contentStack';

type IconsData = {
  allCsIconSvgs: {
    nodes: {
      title: string;
      icon_code: string;
      filled_icon: CsAsset;
      outlined_icon: CsAsset;
    }[];
  };
  /* We only care about the files in the test environment. Due to queries being static this
   * needs to be in the query for all environments. Gatsby has been setup so there should be
   * at least one of these nodes in all environments, so the query won't fail.
   */
  allFile: {
    nodes: {
      publicURL: string;
      extension: string;
      name: string;
    }[];
  };
};

const query = graphql`
  query {
    allCsIconSvgs {
      nodes {
        title
        icon_code
        filled_icon {
          filename
          localAsset {
            publicURL
          }
        }
        outlined_icon {
          localAsset {
            publicURL
          }
        }
      }
    }
  }
`;

const OverflowGlobalStyle = createGlobalStyle`
  /*  We hide the overflow to remove the ability to scroll horizontally, which is induced by using 
      full-width components, such as the PageWidthContainer.
  */
  body {
    overflow-x: hidden;
  }
`;

type ThemeProps = {
  children: React.ReactNode;
  location: Location;
};

const EvoThemeProvider: React.FC<ThemeProps> = ({ children, location }) => {
  const data = useStaticQuery<IconsData>(query);

  let theme = themeMoreThan;
  if (location.pathname.startsWith('/ms-')) {
    theme = themeMarksAndSpencer;
  } else if (location.pathname.startsWith('/tesco-')) {
    theme = themeTesco;
  } else if (location.pathname.startsWith('/jl-')) {
    theme = themeJohnLewis;
  }

  const icons = { ...theme.icons };
  data.allCsIconSvgs.nodes.forEach((icon) => {
    icons[icon.icon_code] = {
      default: icon.filled_icon?.localAsset?.publicURL,
      variant: icon.outlined_icon?.localAsset?.publicURL ?? undefined,
    };
  });

  const themeWithUpdatedIcons = { ...theme, icons };

  return (
    <ThemeProvider theme={themeWithUpdatedIcons}>
      <Helmet>
        <style>{theme.fontFaces}</style>
      </Helmet>
      <GlobalStyle />
      <OverflowGlobalStyle />
      {children}
    </ThemeProvider>
  );
};

export default EvoThemeProvider;
